import { useEffect, useMemo, useRef, useState } from 'react';
import { scaleBand, scaleLinear, scalePoint } from 'd3-scale';
import { area as d3area, line as d3line, curveMonotoneX } from 'd3-shape';
import { useThemeColors } from '../charts/useThemeColors.js';

/**
 * synotis-support#13 — graphiques du détail d'un projet (SVG maison, comme
 * `components/charts/*` : d3-scale + d3-shape, couleurs résolues du thème).
 *
 *  - `ProjectBurnChart` : consommé CHF cumulé (trait plein + aire), projection
 *    consommé + planifié (pointillé), ligne de budget, repère « aujourd'hui ».
 *  - `ProjectHoursChart` : barres saisi vs planifié par bucket.
 *
 * Une seule échelle Y par graphique ; survol = réticule + infobulle HTML.
 */

function useWidth(min) {
  const ref = useRef(null);
  const [width, setWidth] = useState(720);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.max(min, Math.round(w)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [min]);
  return [ref, width];
}

function compact(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString('fr-CH', { maximumFractionDigits: 1 })} M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toLocaleString('fr-CH', { maximumFractionDigits: 1 })} k`;
  return v.toLocaleString('fr-CH', { maximumFractionDigits: 1 });
}

function Legend({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width={18} height={10} aria-hidden="true">
            {it.kind === 'bar' ? (
              <rect x={4} y={0} width={10} height={10} rx={2} fill={it.color} opacity={it.opacity ?? 1} />
            ) : (
              <line x1={0} y1={5} x2={18} y2={5} stroke={it.color} strokeWidth={2} strokeDasharray={it.dash} />
            )}
          </svg>
          {it.label}
        </span>
      ))}
    </div>
  );
}

function Tooltip({ x, width, children }) {
  // Infobulle ancrée au bucket survolé, basculée à gauche près du bord droit.
  const left = x > width - 200 ? undefined : x + 12;
  const right = x > width - 200 ? width - x + 12 : undefined;
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        top: 8,
        left,
        right,
        pointerEvents: 'none',
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
        boxShadow: 'var(--shadow-card)',
        padding: '8px 10px',
        fontSize: 12,
        color: 'var(--text)',
        minWidth: 170,
        zIndex: 2,
      }}
    >
      {children}
    </div>
  );
}

function TipRow({ color, dash, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
        <svg width={12} height={8} aria-hidden="true">
          <line x1={0} y1={4} x2={12} y2={4} stroke={color} strokeWidth={3} strokeDasharray={dash} />
        </svg>
        {label}
      </span>
      <strong style={{ fontWeight: 600 }}>{value}</strong>
    </div>
  );
}

const MARGIN = { top: 14, right: 16, bottom: 30, left: 52 };
const KEYS = ['loggedHours', 'plannedHours'];

export function ProjectBurnChart({ series = [], budgetTotal, today, formatLabel, formatChf, height = 300 }) {
  const colors = useThemeColors();
  const [ref, width] = useWidth(280);
  const [hover, setHover] = useState(null);
  const innerW = Math.max(1, width - MARGIN.left - MARGIN.right);
  const innerH = Math.max(1, height - MARGIN.top - MARGIN.bottom);

  const m = useMemo(() => {
    const x = scalePoint().domain(series.map((d) => d.bucket)).range([0, innerW]).padding(0.5);
    const maxV = Math.max(
      1,
      budgetTotal || 0,
      ...series.map((d) => Math.max(d.cumulativeLoggedChf ?? 0, d.cumulativePlannedChf ?? 0)),
    );
    const y = scaleLinear().domain([0, maxV * 1.05]).nice().range([innerH, 0]);
    const logged = series.filter((d) => d.cumulativeLoggedChf !== null);
    const planned = series.filter((d) => d.cumulativePlannedChf !== null);
    const lineL = d3line().x((d) => x(d.bucket)).y((d) => y(d.cumulativeLoggedChf)).curve(curveMonotoneX);
    const areaL = d3area().x((d) => x(d.bucket)).y0(innerH).y1((d) => y(d.cumulativeLoggedChf)).curve(curveMonotoneX);
    const lineP = d3line().x((d) => x(d.bucket)).y((d) => y(d.cumulativePlannedChf)).curve(curveMonotoneX);
    // Bucket contenant aujourd'hui = dernier bucket dont le début est ≤ aujourd'hui.
    const todayBucket = [...series].reverse().find((d) => d.bucket <= today)?.bucket ?? null;
    const step = Math.ceil(series.length / Math.max(1, Math.floor(innerW / 70)));
    return {
      x,
      y,
      ticks: y.ticks(4),
      loggedPath: lineL(logged) || '',
      areaPath: areaL(logged) || '',
      plannedPath: lineP(planned) || '',
      todayBucket,
      step,
    };
  }, [series, budgetTotal, today, innerW, innerH]);

  const label = (b) => (formatLabel ? formatLabel(b) : b);
  const hovered = hover !== null ? series[hover] : null;
  const band = series.length > 0 ? innerW / series.length : innerW;

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      <Legend
        items={[
          { label: 'Consommé cumulé', color: colors.accent },
          { label: 'Projection (consommé + planifié)', color: colors.accent, dash: '5 4' },
          ...(budgetTotal ? [{ label: 'Budget', color: colors.danger, dash: '2 3' }] : []),
        ]}
      />
      <svg width={width} height={height} role="img" aria-label="Consommation cumulée du budget" style={{ display: 'block' }}>
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {m.ticks.map((t) => (
            <g key={t} transform={`translate(0,${m.y(t)})`}>
              <line x1={0} x2={innerW} stroke={colors.glassBorder} strokeDasharray="2 3" />
              <text x={-8} dy="0.32em" textAnchor="end" fontSize={10} fill={colors.textMuted}>
                {compact(t)}
              </text>
            </g>
          ))}
          {m.todayBucket ? (
            <g transform={`translate(${m.x(m.todayBucket)},0)`}>
              <line y1={0} y2={innerH} stroke={colors.textMuted} strokeDasharray="3 3" />
              <text y={-3} textAnchor="middle" fontSize={9} fill={colors.textMuted}>
                Aujourd’hui
              </text>
            </g>
          ) : null}
          <path d={m.areaPath} fill={colors.accent} opacity={0.14} />
          <path d={m.loggedPath} fill="none" stroke={colors.accent} strokeWidth={2} />
          <path d={m.plannedPath} fill="none" stroke={colors.accent} strokeWidth={2} strokeDasharray="5 4" />
          {budgetTotal ? (
            <g>
              <line x1={0} x2={innerW} y1={m.y(budgetTotal)} y2={m.y(budgetTotal)} stroke={colors.danger} strokeWidth={1.5} strokeDasharray="2 3" />
              <text x={innerW} y={m.y(budgetTotal) - 4} textAnchor="end" fontSize={10} fill={colors.textMuted}>
                Budget {compact(budgetTotal)}
              </text>
            </g>
          ) : null}
          {hovered ? (
            <g>
              <line x1={m.x(hovered.bucket)} x2={m.x(hovered.bucket)} y1={0} y2={innerH} stroke={colors.textMuted} />
              {hovered.cumulativeLoggedChf !== null ? (
                <circle cx={m.x(hovered.bucket)} cy={m.y(hovered.cumulativeLoggedChf)} r={4} fill={colors.accent} stroke={colors.cardBg} strokeWidth={2} />
              ) : null}
              {hovered.cumulativePlannedChf !== null ? (
                <circle cx={m.x(hovered.bucket)} cy={m.y(hovered.cumulativePlannedChf)} r={4} fill={colors.cardBg} stroke={colors.accent} strokeWidth={2} />
              ) : null}
            </g>
          ) : null}
          <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke={colors.glassBorder} />
          {series.map((d, i) =>
            i % m.step === 0 ? (
              <text key={d.bucket} x={m.x(d.bucket)} y={innerH + 14} textAnchor="middle" fontSize={9} fill={colors.textMuted}>
                {label(d.bucket)}
              </text>
            ) : null,
          )}
          {/* Zones de survol (plus larges que les marques). */}
          {series.map((d, i) => (
            <rect
              key={`hit-${d.bucket}`}
              x={m.x(d.bucket) - band / 2}
              y={0}
              width={band}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            />
          ))}
        </g>
      </svg>
      {hovered ? (
        <Tooltip x={MARGIN.left + m.x(hovered.bucket)} width={width}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label(hovered.bucket)}</div>
          {hovered.cumulativeLoggedChf !== null ? (
            <TipRow color={colors.accent} label="Consommé" value={formatChf(hovered.cumulativeLoggedChf)} />
          ) : null}
          {hovered.cumulativePlannedChf !== null ? (
            <TipRow color={colors.accent} dash="3 2" label="Projection" value={formatChf(hovered.cumulativePlannedChf)} />
          ) : null}
          {budgetTotal ? <TipRow color={colors.danger} dash="2 2" label="Budget" value={formatChf(budgetTotal)} /> : null}
        </Tooltip>
      ) : null}
    </div>
  );
}

export function ProjectHoursChart({ series = [], formatLabel, formatHours, height = 240 }) {
  const colors = useThemeColors();
  const [ref, width] = useWidth(240);
  const [hover, setHover] = useState(null);
  const innerW = Math.max(1, width - MARGIN.left - MARGIN.right);
  const innerH = Math.max(1, height - MARGIN.top - MARGIN.bottom);

  const m = useMemo(() => {
    const x = scaleBand().domain(series.map((d) => d.bucket)).range([0, innerW]).padding(0.2);
    const xs = scaleBand().domain(KEYS).range([0, x.bandwidth()]).padding(0.1);
    const maxV = Math.max(1, ...series.map((d) => Math.max(d.loggedHours || 0, d.plannedHours || 0)));
    const y = scaleLinear().domain([0, maxV]).nice().range([innerH, 0]);
    const step = Math.ceil(series.length / Math.max(1, Math.floor(innerW / 60)));
    return { x, xs, y, ticks: y.ticks(4), step };
  }, [series, innerW, innerH]);

  const fill = { loggedHours: colors.accent, plannedHours: colors.textMuted };
  const label = (b) => (formatLabel ? formatLabel(b) : b);
  const hovered = hover !== null ? series[hover] : null;

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      <Legend
        items={[
          { label: 'Saisi', color: colors.accent, kind: 'bar' },
          { label: 'Planifié', color: colors.textMuted, kind: 'bar', opacity: 0.55 },
        ]}
      />
      <svg width={width} height={height} role="img" aria-label="Heures saisies et planifiées par période" style={{ display: 'block' }}>
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {m.ticks.map((t) => (
            <g key={t} transform={`translate(0,${m.y(t)})`}>
              <line x1={0} x2={innerW} stroke={colors.glassBorder} strokeDasharray="2 3" />
              <text x={-8} dy="0.32em" textAnchor="end" fontSize={10} fill={colors.textMuted}>
                {formatHours(t, true)}
              </text>
            </g>
          ))}
          {series.map((d, i) => (
            <g key={d.bucket} transform={`translate(${m.x(d.bucket)},0)`}>
              {KEYS.map((k) => {
                const v = d[k] || 0;
                return (
                  <rect
                    key={k}
                    x={m.xs(k)}
                    y={m.y(v)}
                    width={m.xs.bandwidth()}
                    height={Math.max(0, innerH - m.y(v))}
                    fill={fill[k]}
                    opacity={k === 'plannedHours' ? 0.55 : hover === null || hover === i ? 1 : 0.6}
                    rx={2}
                  />
                );
              })}
              <rect
                x={-m.x.step() * m.x.paddingInner() / 2}
                y={0}
                width={m.x.step()}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
              />
            </g>
          ))}
          <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke={colors.glassBorder} />
          {series.map((d, i) =>
            i % m.step === 0 ? (
              <text key={d.bucket} x={m.x(d.bucket) + m.x.bandwidth() / 2} y={innerH + 14} textAnchor="middle" fontSize={9} fill={colors.textMuted}>
                {label(d.bucket)}
              </text>
            ) : null,
          )}
        </g>
      </svg>
      {hovered ? (
        <Tooltip x={MARGIN.left + m.x(hovered.bucket) + m.x.bandwidth() / 2} width={width}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label(hovered.bucket)}</div>
          <TipRow color={colors.accent} label="Saisi" value={formatHours(hovered.loggedHours)} />
          <TipRow color={colors.textMuted} label="dont facturable" value={formatHours(hovered.billableHours)} />
          <TipRow color={colors.textMuted} label="Planifié" value={formatHours(hovered.plannedHours)} />
        </Tooltip>
      ) : null}
    </div>
  );
}
